<script setup lang="ts">
import { computed } from 'vue'
import * as formatNumber from '@/helpers/numberFormatters'
import { useScreen } from '@/composables/useScreen'
import { currentUser } from '@/lib/currentUser'
import TeacherManageTeachingRequests from '../TeacherManageTeachingRequests.vue'
import { useStaticAsset } from '@/composables/useStaticAsset'

const asset = useStaticAsset()

interface Context {
  general: { id: number; [key: string]: unknown }
  online_teaching?: {
    visible: boolean
    accept_requests: boolean
    trial_price: number
    price_min: number
    price_max: number
    native_speaker: boolean
    using_pikimoni: boolean
    software: Array<{ id: number; name: string }>
    payment_methods: Array<{ name: string }>
    offer_id: number
  } | null
  [key: string]: unknown
}

const props = defineProps<{
  context: Context
  isCurrentUser: boolean
}>()

const { isMobile } = useScreen()

const currentProfileIsCurrentUser = computed(() => props.context.general.id === currentUser.id)
const currentUserIsTeacher = computed(() => currentUser.isTeacher)

const formatPrice = (value: number) => formatNumber.price.rub(value)

function onRequestsUpdated() {
  window.location.reload()
}
</script>

<template>
  <div class="d-flex flex-column">
    <div class="d-flex justify-space-between align-center">
      <h2 class="text-title-bold d-flex">Онлайн-обучение</h2>

      <v-menu-base v-if="currentProfileIsCurrentUser" pos-horizontal="right" :close-on-inner-click="false">
        <template #activator="{ onclick }">
          <v-link class="ml-2" @click="onclick">
            <v-icon class="gray-text" size="20">mdi-pencil</v-icon>
          </v-link>
        </template>

        <div class="pa-1">
          <TeacherManageTeachingRequests
            v-if="context.online_teaching !== null"
            class="ma-half mb-2"
            :offer-visible="context.online_teaching!.visible"
            :offer-active="context.online_teaching!.accept_requests"
            @updated="onRequestsUpdated"
          ></TeacherManageTeachingRequests>

          <hr v-if="context.online_teaching !== null" />

          <v-menu-button mode="primary" href="/online_teaching/trial_lesson_offer">
            <v-icon class="mr-1" size="20">mdi-file-edit-outline</v-icon>
            Изменить анкету
          </v-menu-button>
        </div>
      </v-menu-base>
    </div>

    <div v-if="!context.online_teaching || !context.online_teaching.accept_requests" class="my-2">
      Сейчас учитель не принимает заявки на обучение.
    </div>

    <div v-else-if="isMobile" class="d-flex flex-column">
      <div class="d-flex my-2">
        <div class="d-flex flex-column flex-spacer">
          <span class="gray-text mb-1">Пробный урок</span>
          <span class="text-title-bold">
            {{ formatPrice(context.online_teaching.trial_price) }}
          </span>
        </div>

        <div class="mx-1"></div>

        <div class="d-flex flex-column flex-spacer">
          <span class="gray-text mb-1">Урок</span>
          <span class="text-title-bold"> от {{ formatPrice(context.online_teaching.price_min) }} </span>
          <span class="text-caption"> до {{ formatPrice(context.online_teaching.price_max) }} </span>
        </div>
      </div>

      <div class="d-flex my-2">
        <div class="d-flex flex-column flex-spacer">
          <span class="gray-text mb-1">Носитель языка</span>
          <span v-if="!context.online_teaching.native_speaker" class="text-title-bold"> Нет </span>
          <template v-else>
            <span class="text-title-bold">Есть</span>
            <span class="text-caption">Привлеченный</span>
          </template>
        </div>

        <div class="mx-1"></div>

        <div class="d-flex flex-column flex-spacer">
          <span class="gray-text mb-1">Pikimoni</span>
          <span v-if="!context.online_teaching.using_pikimoni" class="text-title-bold"> Нет </span>
          <template v-else>
            <span class="text-title-bold">Есть</span>
            <img :src="asset('img/pikimoni.svg')" height="32" />
          </template>
        </div>
      </div>

      <span class="gray-text mb-1 mt-2"> Программы </span>

      <div v-for="software in context.online_teaching.software" :key="software.id" class="d-flex align-center mb-1">
        <v-icon size="20" class="mr-1"> soft-{{ software.name.toLowerCase().replace(/\s/g, '-') }} </v-icon>
        {{ software.name }}
      </div>

      <span class="text-caption gray-text"> Подойдет одна из этих программ </span>
    </div>

    <table v-else class="teacher-bio__tariffs-table mt-2">
      <tbody>
        <tr>
          <th>Пробный урок</th>
          <th>Урок</th>
          <th>Pikimoni</th>
          <th>Носитель языка</th>
          <th>Программы</th>
        </tr>

        <tr>
          <td>
            {{ formatPrice(context.online_teaching.trial_price) }}
          </td>

          <td>
            от {{ formatPrice(context.online_teaching.price_min) }}<br />
            <span class="text-caption">до {{ formatPrice(context.online_teaching.price_max) }}</span>
          </td>

          <td v-if="context.online_teaching.using_pikimoni">
            Есть<br />
            <img :src="asset('img/pikimoni.svg')" height="32" />
          </td>
          <td v-else>Нет</td>

          <td v-if="context.online_teaching.native_speaker">
            Есть<br />
            <span class="text-caption">Привлеченный</span>
          </td>
          <td v-else>Нет</td>

          <td>
            <div class="text-body d-flex flex-column">
              <div
                v-for="software in context.online_teaching.software"
                :key="software.id"
                class="d-flex align-center mb-1"
              >
                <v-icon size="20" class="mr-1"> soft-{{ software.name.toLowerCase().replace(/\s/g, '-') }} </v-icon>
                {{ software.name }}
              </div>

              <span class="text-caption gray-text"> Подойдет одна из этих программ </span>
            </div>
          </td>
        </tr>

        <tr>
          <td colspan="5">
            <span class="text-caption gray-text">Способы оплаты</span><br />
            <span class="text-body">
              {{ context.online_teaching.payment_methods.map(e => e.name).join(', ') }}
            </span>
          </td>
        </tr>
      </tbody>
    </table>

    <div class="d-flex mt-2">
      <div v-if="!isMobile" class="flex-spacer"></div>

      <v-button
        v-if="!currentProfileIsCurrentUser && context.online_teaching && !currentUserIsTeacher"
        mode="primary filled"
        :href="`/online_teaching/trial_lesson_request/${context.online_teaching.offer_id}`"
        target="_blank"
      >
        Записаться на пробный урок
      </v-button>
    </div>
  </div>
</template>
